import React from "react";
import styled from "styled-components";
import { getFieldValue } from "@sitecore-jss/sitecore-jss-react";
import { Button } from "components/components/atoms";
import { Container } from "components/shared";
import useBreakpoint from "utils/hooks/useBreakpoint";
import breakpoints from "utils/breakpoints";
import tw from "tailwind.macro";
import { scrollToSection } from "utils/scrollTo";

const BreadcrumbContainer = styled.div`
  height: 4.375rem;

  @media ${breakpoints.md} {
    ${tw`h-90-mattamy`}
  }
`;

const StyledButton = styled(Button)`
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 3rem;
  padding-right: 3rem;
  height: 3.125rem;
  white-space: nowrap;
`;

const getCTA = (rendering) => {
  try {
    const fields = rendering.placeholders["btn-cta"][0].fields;
    return getFieldValue(fields, "buttonCta");
  } catch (e) {
    console.log(e);
    return {};
  }
};
const getBreadcrumText = (text, l) => {
  if (text) {
    return text.length > l ? text.substring(0, l) + "... " : text;
  }
  return "";
};
const removeBackTo = (text) => {
  if (text && text.length > 0) return text.replace("Back to ", "");
  return text;
};
const getMediumLength = (breadcrumList, breakpoint) => {
  let totalLetters = 0;
  let getMax = 0;
  let lastLength = 0;
  for (let i = 1; i < breadcrumList.length; i++) {
    const text = removeBackTo(breadcrumList[i].text);
    getMax = getMax < text.length ? text.length : getMax;
    totalLetters = totalLetters + text.length;
    if (i === breadcrumList.length - 1) lastLength = text.length;
  }
  if (breakpoint == "md") {
    if (totalLetters <= 45) return getMax;
    return (45 - lastLength) / (breadcrumList.length - 2);
  }
  if (breakpoint == "lg") {
    if (totalLetters <= 70) return getMax;
    return (70 - lastLength) / (breadcrumList.length - 2);
  }
  if (breakpoint == "xl") {
    if (totalLetters <= 90) return getMax;
    return (90 - lastLength) / (breadcrumList.length - 2);
  }
  if (breakpoint == "2xl") {
    if (totalLetters <= 110) return getMax;
    return (110 - lastLength) / (breadcrumList.length - 2);
  }
  return 8;
};
const Breadcrumb = ({ fields, rendering }) => {
  const breadcrumbs = getFieldValue(fields, "breadcrumbs") || [];
  const backItem = breadcrumbs[breadcrumbs.length - 2] || {};
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);

  const handleCTAClick = () => {
    scrollToSection("contact");
    window.dataLayer.push({ event: "register" });
  };

  const cta = getCTA(rendering);
  const UTourCTA = getFieldValue(fields, "uTourFirstCtaLink");
  const substringLength = getMediumLength(breadcrumbs, breakpoint);
  //console.log({breadcrumbs, breakpoint, substringLength})
  //console.log({rendering})

  return (
    <BreadcrumbContainer id="breadcrumb" className="w-full bg-light-blue">
      <Container className="flex flex-col md:flex-row justify-between items-start md:items-center py-0 md:py-5">
        <div className="flex items-center justify-start py-4 text-mattamy-blue font-trade-gothic-20 text-base tracking-wider uppercase">
          <div className="self-center pr-5 mt-1 flex ">
            {breadcrumbs && isMobile ? (
              <span key={backItem.text}>
                <a href={backItem.href} aria-label={"Go to " + backItem.text}>
                  {backItem.text}
                </a>
              </span>
            ) : (
              breadcrumbs.map(
                (breadcrumb, i) =>
                  i !== 0 && (
                    <div key={i} className="relative flex justify-end">
                      {i === 1 || i === 3 ? (
                        getBreadcrumText(
                          removeBackTo(breadcrumb.text),
                          substringLength
                        )
                      ) : i < breadcrumbs.length - 1 && breadcrumb.href ? (
                        <a
                          href={breadcrumb.href}
                          aria-label={breadcrumb.text}
                          className={"hover:underline"}
                        >
                          {getBreadcrumText(breadcrumb.text, substringLength)}
                        </a>
                      ) : (
                        breadcrumb.text
                      )}
                      {i < breadcrumbs.length - 1 && (
                        <svg
                          className={"h-5 mb-1 transform rotate-180"}
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            fill="currentColor"
                            d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"
                          />
                        </svg>
                      )}
                    </div>
                  )
              )
            )}
          </div>
        </div>

        <div className="hidden md:flex h-auto">
          {UTourCTA && UTourCTA.text && (
            <StyledButton
              className="mr-2"
              aria-label={UTourCTA.text}
              role="link"
              color="action-blue"
              smallCTA
              href={UTourCTA.href}
              target={UTourCTA.target}
            >
              {UTourCTA && UTourCTA.text}
            </StyledButton>
          )}

          {cta && cta.text && (
            <StyledButton
              aria-label={cta.text}
              role="link"
              color="action-blue"
              onClick={handleCTAClick}
              smallCTA
            >
              {cta && cta.text}
            </StyledButton>
          )}
        </div>
      </Container>
    </BreadcrumbContainer>
  );
};

export default Breadcrumb;
